import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { useTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { m } from 'framer-motion';
import { CarouselDots } from '../../components/carousel';
import Image from '../../components/Image';
import useLocales from '../../hooks/useLocales';

const VipCarousel = ({ currentIndex, setCurrentIndex, cards, carouselRef }) => {
  const theme = useTheme();
  const detailsRef = useRef(null);

  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);

  const settings = {
    asNavFor: nav2,
    ref: (slider) => {
      setNav1(slider);
      if (carouselRef) {
        carouselRef.current = slider;
      }
    },
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '40px',
    arrows: false,
    infinite: false,
    beforeChange: (_, next) => setCurrentIndex(next),
  };

  const detailsSettings = {
    asNavFor: nav1,
    ref: (slider) => {
      setNav2(slider);
      if (carouselRef) {
        carouselRef.current = slider;
      }
    },
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    infinite: false,
    swipe: true,
  };

  return (
    <Box
      sx={{
        background: 'none',
        '& .slick-active': { zIndex: 999 },
        '& .slick-list': { overflow: 'unset' },
        '& .slick-prev': {
          bottom: '-100px',
          left: 0,
          width: '50px',
          height: '50px',
        },
        '& .slick-next': {
          top: 'unset',
          bottom: '-100px',
          right: 0,
          width: '50px',
          height: '50px',
        },
        my: 2,
        borderRadius: 0,
        width: '100%',
      }}
    >
      <Slider ref={carouselRef} {...settings}>
        {cards.map((level, index) => (
          <CarouselItem key={index} level={level} isActive={index === currentIndex} />
        ))}
      </Slider>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: '4px' }}>
        {cards.map((_, i) => (
          <Box
            key={i}
            onClick={() => {
              setCurrentIndex(i);
              if (carouselRef.current) carouselRef.current.slickGoTo(i);
              if (detailsRef.current) detailsRef.current.slickGoTo(i);
            }}
            sx={{
              width: 10,
              height: 2,
              borderRadius: 3,
              bgcolor: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.3)',
              transition: '0.3s',
              cursor: 'pointer',
            }}
          />
        ))}
      </Box>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: 3,
        }}
      >
        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 18 }}>VIP Details</Typography>
      </Box>

      {/* Details Carousel */}
      <Slider ref={detailsRef} {...detailsSettings} style={{ marginTop: 24 }}>
        {cards.map((level, index) => (
          <VipDetailsList key={index} details={level.details} />
        ))}
      </Slider>
    </Box>
  );
};

VipCarousel.propTypes = {
  currentIndex: PropTypes.number.isRequired,
  setCurrentIndex: PropTypes.func.isRequired,
  cards: PropTypes.array.isRequired,
};

export default VipCarousel;

function CarouselItem({ level, isActive }) {
  const { translate } = useLocales();
  return (
    <Box
      component={m.div}
      transition={{ duration: 0.4 }}
      sx={{
        position: 'relative',
        mx: 1,
        mt: 5,
      }}
    >
      {/* Border */}
      <Box
        sx={{
          p: '1px',
          borderRadius: 3,
          background: 'linear-gradient(135deg, #AC8B80, #B99282, #C5A896)',
        }}
      >
        <Box
          sx={{
            borderRadius: 3,
            p: 2,
            pr: 12,
            height: 'clamp(130px, 20vh, 180px)',
            color: '#fff',
            backgroundImage: `url(${level.cardBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            boxShadow: isActive ? '0 10px 30px rgba(0,0,0,0.6)' : 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: 'clamp(18px, 2.5vh, 28px)',
              fontWeight: 500,
              lineHeight: '100%',
              textTransform: 'uppercase',
              display: 'inline-block',
              background: `linear-gradient(90deg, ${level.titleColor.join(', ')})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {level.name}
          </Typography>

          <Typography
            sx={{
              fontSize: 'clamp(10px, 1.3vw, 13px)',
              opacity: 0.7,
              color: level.subColor,
            }}
          >
            {translate('valid_until')} 2026.10.28
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontSize: 'clamp(10px, 1.3vw, 13px)',
              color: level.subColor,
            }}
          >
            {translate('maintenance_requirement')}
          </Typography>

          <Box
            sx={{
              height: 6,
              borderRadius: 5,
              mt: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
            }}
          >
            <Box
              sx={{
                width: '50%',
                height: '100%',
                borderRadius: 5,
                backgroundColor: '#ffb36b',
              }}
            />
          </Box>

          <Box sx={{ fontSize: 12, mt: 0.5, color: level.subColor }}>
            {`${level.maintenanceRequirementValue.toLocaleString()} / ${level.maintenanceRequirementMax.toLocaleString()}`}
          </Box>
        </Box>
      </Box>

      <Box
        component="img"
        src={level.image}
        alt={level.name}
        sx={{
          position: 'absolute',
          right: -20,
          top: '-10vh',
          height: '25vh',
          pointerEvents: 'none',
          transition: '0.4s',
        }}
      />
    </Box>
  );
}

function VipDetailsList({ details }) {
  const { translate } = useLocales();
  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    p: 1,
  };
  const labelStyle = { color: '#FFFFFFCC', fontWeight: 400, fontSize: 14 };
  const valueStyle = { color: '#fff', fontWeight: 600, fontSize: 14 };

  const items = [
    { label: 'daily_rebate', value: details.dailyRebate },
    { label: 'withdraw_count', value: details.withdrawCount },
    { label: 'weekly_rebate', value: details.weeklyRebate },
    { label: 'tier_bonus_upgrade', value: details.tierBonus },
    { label: 'special_birthday_gift', value: details.birthdayGift },
    { label: 'birthday_bonus', value: details.birthdayBonus },
    { label: 'upgrade_next_tier', value: details.upgradeNext },
    { label: 'membership_renewal', value: details.renewal },
  ];

  return (
    <Box sx={{ mt: 1, p: 1, bgcolor: '#111', borderRadius: 2, mx: 2 }}>
      {items.map((item, idx) => (
        <Box key={idx} sx={rowStyle}>
          <Typography sx={labelStyle}>{translate(item.label)}</Typography>
          <Typography sx={valueStyle}>{item.value}</Typography>
        </Box>
      ))}
    </Box>
  );
}
